import { useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { makeStyles, tokens, Spinner, Text, Button } from '@fluentui/react-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import isEqual from 'lodash.isequal';
import { WorkflowDesignerWithCode } from '@/components/WorkflowDesigner';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import { useRecentWorkStore } from '@/stores/recentWorkStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useWorkflowViewPrefsStore, composeWorkflowKey } from '@/stores/workflowViewPrefsStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useDraftModeStore } from '@/stores/draftModeStore';
import { useAgentChatAguiStore } from '@/stores/agentChatAguiStore';
import { useEditorRunStore } from '@/stores/editorRunStore';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useScopeCollapseStore } from '@/stores/scopeCollapseStore';
import { useMonitoringStore } from '@/stores/monitoringStore';
import { useLayout } from '@/contexts';
import { paths } from '@/routes';
import { fetchWorkflowArtifacts, fetchParametersJson, fetchParametersDraftJson } from '@/services/logicAppsApi';
import { getDraftWorkflow } from '@/services/draftWorkflowApi';
import { getNodesMetadata } from '@/services/nodesMetadataApi';
import { applySidecarBranch, emptyNodesMetadata, type NodesMetadataFile } from '@/services/nodesMetadata';
import { setCachedSidecar } from '@/services/nodesMetadataCache';
import { getMockData } from '@/services/mockDataApi';
import { emptyMockData, type MockDataFile } from '@/services/mockData';
import { setCachedMockData } from '@/services/mockDataCache';
import { isFileModifiedBefore } from '@/services/vfsService';
import { useAppLevelParameters } from '@/hooks/useAppLevelParameters';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { type ConnectionsConfiguration } from '@/types/logicApps';
import { WorkflowDefinitionSchema, type WorkflowDefinition } from '@/types/workflowDefinition';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: tokens.spacingHorizontalM,
  },
  error: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: tokens.spacingVerticalM,
    color: tokens.colorPaletteRedForeground1,
  },
});

export const WorkflowDesignerPage = () => {
  const styles = useStyles();
  const { tenantId, locationName, projectName: urlProject, appName: urlApp, workflowName, runId } = useParams<{
    tenantId: string; locationName: string; projectName: string; appName: string; workflowName: string; runId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getOttoToken, account } = useAuth();
  const { selectedApp, selectedProject: storeProject } = useAppStore();
  const selectedTenantId = useAppStore((s) => s.selectedTenantId);

  // Use URL params as primary source, store as fallback
  const projectName = urlProject ?? storeProject;
  const appName = urlApp ?? selectedApp?.name;

  // Browser tab title: workflow > app > project context. Prefix "Run" for
  // monitoring view (runId in path). runId itself is omitted — too long/noisy
  // for a tab.
  useDocumentTitle(
    runId
      ? ['Run', workflowName, appName, projectName]
      : [workflowName, appName, projectName],
  );
  
  const { setWorkflowFiles, clearWorkflow, workflow: storeWorkflow } = useWorkflowStore();
  const { syncFromConnectionsConfig, setMappingFromWorkflow, clearConnections } = useConnectionStore();
  const { initializeArtifacts, reset: resetDraftMode } = useDraftModeStore();
  const setAgentMode = useAgentChatAguiStore((state) => state.setAgentMode);
  const { setBreadcrumbs } = useLayout();

  // Warm the `parameters.json` cache as soon as the designer mounts so the
  // dynamic-content picker and ExpressionPreview have parameter values ready
  // by the time the user opens their first node (no in-popover loading flash).
  useAppLevelParameters();

  // Set agent mode based on URL query param
  // ?mode=classic -> classic mode, otherwise agent mode (default)
  useEffect(() => {
    const mode = searchParams.get('mode');
    // Only set to classic mode if explicitly requested, otherwise default to agent mode
    setAgentMode(mode !== 'classic');
  }, [searchParams, setAgentMode]);

  // Set up breadcrumbs
  useEffect(() => {
    setBreadcrumbs([
      {
        label: workflowName ?? 'Workflow',
        type: 'workflow',
        meta: { project: projectName ?? undefined },
      },
    ]);

    // Track recent workflow access for the Home page
    if (workflowName && appName && projectName && locationName) {
      try {
        // Use route-param tenantId for href (preserves URL shape),
        // fall back to store for the entry's tenantId field (always needed for filtering)
        const entryTenantId = tenantId ?? useAppStore.getState()?.selectedTenantId;
        if (entryTenantId) {
          useRecentWorkStore.getState().recordAccess({
            type: 'workflow',
            displayName: workflowName,
            location: locationName,
            project: projectName,
            app: appName,
            workflow: workflowName,
            href: paths.appWorkflowDesigner(locationName, projectName, appName, workflowName, tenantId),
            tenantId: entryTenantId,
          });
        }
      } catch {
        // Store may not be initialized in test environments
      }
    }

    return () => {
      setBreadcrumbs([]);
    };
  }, [workflowName, projectName, setBreadcrumbs, appName, locationName, tenantId]);

  // Clear query cache when workflow changes to ensure fresh data
  // Following portal pattern: clearDataCache() before opening designer
  useEffect(() => {
    if (workflowName && appName && projectName) {
      queryClient.invalidateQueries({ queryKey: ['workflowDesigner', projectName?.toLowerCase(), appName?.toLowerCase(), workflowName] });
    }
  }, [workflowName, appName, projectName, queryClient]);

  const { data, isLoading, error } = useQuery({
    // Use a different query key to avoid conflicts with useWorkflowConnectionReferences
    // which caches workflow definitions in a different format
    queryKey: ['workflowDesigner', projectName?.toLowerCase(), appName?.toLowerCase(), workflowName],
    queryFn: async (): Promise<{
      prodWorkflow: WorkflowDefinition;
      prodConnections: ConnectionsConfiguration | null;
      prodParameters: Record<string, unknown> | null;
      prodNotes: Record<string, unknown> | null;
      draftWorkflow: WorkflowDefinition | null;
      draftConnections: ConnectionsConfiguration | null;
      draftParameters: Record<string, unknown> | null;
      draftNotes: Record<string, unknown> | null;
      hasDraftChanges: boolean;
      isProdNewerThanDraft: boolean;
      sidecar: NodesMetadataFile | null;
      mockData: MockDataFile | null;
    }> => {
      if (!projectName || !appName || !workflowName) {
        throw new Error('No Logic App selected');
      }

      // Kick off the nodesMetadata sidecar fetch up front so it runs in
      // parallel with `fetchWorkflowArtifacts` and any follow-up
      // `getDraftWorkflow` call — it has no dependency on either. Failure
      // is non-fatal (handled where the value is awaited below).
      const sidecarPromise = getNodesMetadata({
        projectName,
        appName,
        getToken: getOttoToken,
        workflowName,
      }).catch(() => null);

      // Likewise the draft-only mock sidecar — fetched in parallel, applied
      // to the store only when the loaded view is draft (see effect below).
      const mockDataPromise = getMockData({
        projectName,
        appName,
        getToken: getOttoToken,
        workflowName,
      }).catch(() => null);

      // Fetch app-level parameters in parallel with the workflow artifacts.
      // These are app-scoped (not workflow-scoped) but hydrate the draft mode
      // store so `usePublishDraft` can forward `draftParameters` to the
      // publish endpoint. Without this, parameters-draft.json edits made
      // outside the in-portal Parameters editor (e.g., hand-edits on disk)
      // are silently dropped on publish.
      const appCtx = { projectName, appName, getToken: getOttoToken };
      const prodParametersPromise = fetchParametersJson(appCtx).catch(() => ({} as Record<string, unknown>));
      const draftParametersPromise = fetchParametersDraftJson(appCtx).catch(() => ({} as Record<string, unknown>));

      // Fetch production artifacts via ARM API with $expand
      const { files } = await fetchWorkflowArtifacts({
        projectName,
        appName,
        getToken: getOttoToken,
        workflowName,
        includeDraft: true,
      });

      if (!files?.['workflow.json']) {
        throw new Error('Workflow definition not found');
      }

      // Helper to parse workflow JSON
      const parseWorkflow = (workflowJson: unknown): WorkflowDefinition => {
        // Handle case where API returns definition directly (without wrapper)
        const workflowData = (workflowJson as Record<string, unknown>)?.definition
          ? workflowJson // Already has definition property
          : { definition: workflowJson }; // Wrap raw definition

        const parsed = WorkflowDefinitionSchema.safeParse(workflowData);

        if (!parsed.success) {
          console.error('Workflow validation failed:', parsed.error);
          // Return structured data for graceful degradation
          // workflowData always has a `definition` wrapper at this point
          const defn = (workflowData as Record<string, unknown>)?.definition as Record<string, unknown> | undefined;
          return {
            definition: {
              triggers: defn?.triggers ?? {},
              actions: defn?.actions ?? {},
              contentVersion: '1.0.0.0',
            },
          } as WorkflowDefinition;
        }
        return parsed.data;
      };

      // Parse production workflow
      const prodWorkflow = parseWorkflow(files['workflow.json']);
      
      const prodConnections = (files['connections.json'] as ConnectionsConfiguration) ?? null;
      const prodNotes = (files['notes.json'] as Record<string, unknown>) ?? null;

      // Try to get draft workflow from ARM API response first
      let draftWorkflowJson: unknown = files['workflow-draft.json'];
      let draftConnections: ConnectionsConfiguration | null = (files['connections-draft.json'] as ConnectionsConfiguration) ?? null;
      let draftNotes: Record<string, unknown> | null = (files['notes-draft.json'] as Record<string, unknown>) ?? null;
      let isProdNewerThanDraft = false;

      // If draft workflow not in ARM response, fetch it separately via getDraftWorkflow
      // This is needed because workflow-draft.json is stored in a subfolder and may not
      // be returned by the ARM $expand parameter
      if (!draftWorkflowJson) {
        try {
          const draftResult = await getDraftWorkflow({
            projectName,
            appName,
            getToken: getOttoToken,
            workflowName,
          });

          if (draftResult.workflow) {
            draftWorkflowJson = draftResult.workflow;
          }
          if (draftResult.connections) {
            draftConnections = draftResult.connections;
          }
          if (draftResult.notes) {
            draftNotes = draftResult.notes;
          }

          // Stale draft detection using VFS timestamps
          // If production was modified AFTER the draft, the draft is stale and should be discarded
          if (draftResult.vfsFiles) {
            isProdNewerThanDraft = isFileModifiedBefore(
              draftResult.vfsFiles,
              'workflow-draft.json', // first file (draft)
              'workflow.json' // second file (production)
            );

            // If draft is stale, discard it
            if (isProdNewerThanDraft) {
              console.log('[WorkflowDesignerPage] Discarded stale draft - production is newer');
              draftWorkflowJson = undefined;
              draftConnections = null;
              draftNotes = null;
            }
          }
        } catch (_error) {
          // Draft doesn't exist or couldn't be fetched - this is fine
        }
      }

      const draftWorkflow = draftWorkflowJson ? parseWorkflow(draftWorkflowJson) : null;

      // Resolve the parameter fetches. Normalize empty objects to null so the
      // store's `??` fallbacks in `initializeArtifacts` treat "no params" the
      // same regardless of cloud (returns prod content as fallback) vs local
      // (returns `{}`).
      const [prodParamsRaw, draftParamsRaw] = await Promise.all([prodParametersPromise, draftParametersPromise]);
      const prodParameters = prodParamsRaw && Object.keys(prodParamsRaw).length > 0 ? prodParamsRaw : null;
      const draftParameters = draftParamsRaw && Object.keys(draftParamsRaw).length > 0 ? draftParamsRaw : null;

      // Determine if draft has unpublished changes
      // Draft exists AND differs from production AND NOT stale = has changes
      // Also check connections: draft connections may have new entries (e.g., newly created
      // OAuth connections) even if the workflow definition hasn't changed yet.
      // Also check parameters: parameters-draft.json may contain values not yet in parameters.json
      // (e.g., hand-edited on disk or saved via the in-portal editor).
      const hasWorkflowChanges = draftWorkflow !== null && !isEqual(draftWorkflow, prodWorkflow) && !isProdNewerThanDraft;
      const hasConnectionChanges = draftConnections !== null && !isEqual(draftConnections, prodConnections) && !isProdNewerThanDraft;
      const hasParameterChanges = draftParameters !== null && !isEqual(draftParameters, prodParameters) && !isProdNewerThanDraft;
      const hasDraftChanges = hasWorkflowChanges || hasConnectionChanges || hasParameterChanges;

      // Resolve the sidecar fetch we started in parallel above. Failure is
      // non-fatal — the workflow renders fine without it, and the next save
      // will create it from the current in-memory state.
      const sidecar = await sidecarPromise;
      const mockData = await mockDataPromise;

      return {
        prodWorkflow,
        prodConnections,
        prodParameters,
        prodNotes,
        draftWorkflow,
        draftConnections,
        draftParameters,
        draftNotes,
        hasDraftChanges,
        isProdNewerThanDraft,
        sidecar,
        mockData,
      };
    },
    enabled: !!projectName && !!appName && !!workflowName,
    // Don't cache across unmounts - always fetch fresh draft data when re-opening a workflow
    // This prevents showing a stale draft after autosave writes a newer version
    gcTime: 0,
    // CRITICAL: Prevent refetches during editing session to avoid overwriting user changes
    // The workflow is managed by workflowStore after initial load - we don't want React Query
    // to refetch and reset the store while the user is editing
    staleTime: Infinity,
  });

  // Update stores when data loads
  // Following portal pattern: initialize both draft and production artifacts
  // IMPORTANT: This effect should only run ONCE for a given workflow load.
  // If `data` gets a new object reference (e.g., from React Query refetch or cache update),
  // we must NOT overwrite the workflow store, as that would discard any user changes.
  useEffect(() => {
    // Skip if no data
    if (!data) return;

    // If we've already synced this data but the Azure region just resolved,
    // update only the stored location (don't re-sync the entire workflow).
    if (data === lastSyncedDataRef.current) {
      return;
    }

    // SAFETY: If we've completed initial sync, don't overwrite the store.
    // The user may have made edits that aren't saved yet.
    // This is a defense-in-depth check in case React Query somehow refetches despite our settings.
    if (hasInitialSyncCompletedRef.current) {
      // Check the current store state directly (not via dependency)
      const currentWorkflow = useWorkflowStore.getState().workflow;
      if (currentWorkflow) {
        const storeHasContent =
          Object.keys(currentWorkflow.definition?.triggers ?? {}).length > 0 ||
          Object.keys(currentWorkflow.definition?.actions ?? {}).length > 0;
        if (storeHasContent) {
          console.warn('[WorkflowDesignerPage] Skipping sync - store already has content from user edits');
          lastSyncedDataRef.current = data; // Mark as synced to prevent repeated warnings
          return;
        }
      }
      // Debug: Log if we're about to overwrite after initial sync completed
      console.warn('[WorkflowDesignerPage] WARNING: Initial sync completed but store is empty - this may cause data loss!');
    }

    const { prodWorkflow, prodConnections, prodParameters, prodNotes, draftWorkflow, draftConnections, draftParameters, draftNotes, hasDraftChanges } = data;

    // Initialize draft mode store with both production and draft artifacts (including notes + parameters).
    // Parameters are app-scoped, but hydrating them here is the single entry point that lets
    // `usePublishDraft` forward `draftParameters` to the publish endpoint. Without this, the publish
    // call drops the `parametersConfig` field and the backend deletes `parameters-draft.json` without
    // writing the values to `parameters.json`.
    initializeArtifacts(
      { workflow: prodWorkflow, connections: prodConnections, parameters: prodParameters, notes: prodNotes },
      hasDraftChanges
        ? { workflow: draftWorkflow ?? prodWorkflow, connections: draftConnections ?? prodConnections, parameters: draftParameters ?? prodParameters, notes: draftNotes ?? prodNotes }
        : undefined // No separate draft = use production for both
    );

    // Load into workflow store:
    // - If draft exists with changes, load the DRAFT (resume editing)
    // - Otherwise, load production
    const workflowToLoad = hasDraftChanges && draftWorkflow ? draftWorkflow : prodWorkflow;
    const connectionsToLoad = hasDraftChanges ? (draftConnections ?? prodConnections) : prodConnections;
    const notesToLoad = hasDraftChanges && draftNotes ? draftNotes : prodNotes;

    // Merge notes into workflow (notes are stored in separate file but part of workflow schema)
    const workflowWithNotes = notesToLoad
      ? { ...workflowToLoad, notes: notesToLoad as WorkflowDefinition['notes'] }
      : workflowToLoad;

    setWorkflowFiles(workflowWithNotes, connectionsToLoad);

    // Sync connection references from connections to connection store
    syncFromConnectionsConfig(connectionsToLoad);
    // Rebuild the node→reference mapping from the loaded workflow so it reflects
    // which connections THIS workflow uses (syncFromConnectionsConfig only loads
    // references and preserves mapping, leaving it empty on a fresh load).
    setMappingFromWorkflow(workflowWithNotes);

    // Mark this data as synced to prevent re-running on same data
    lastSyncedDataRef.current = data;
  }, [data, setWorkflowFiles, syncFromConnectionsConfig, setMappingFromWorkflow, initializeArtifacts]);

  // Hydrate per-workflow view preferences (layout orientation) and set the
  // current workflow identity on the store. Runs in its own effect — NOT
  // gated by `lastSyncedDataRef === data` — because the tenant id may be
  // auto-selected AFTER the workflow data loads on bare `/location/...`
  // routes. Without a reactive dependency on `selectedTenantId`, the first
  // sync pass would observe no tenant, mark the data as synced, and never
  // re-hydrate.
  //
  // Bypasses `setLayoutOrientation` and writes directly via `setState` so the
  // write-through to the prefs store doesn't fire (we're reading from prefs,
  // not writing). Subsequent user toggles go through `setLayoutOrientation`,
  // which writes through normally. The write is atomic — orientation and
  // identity move together.
  const userId = account?.localAccountId ?? account?.homeAccountId;
  useEffect(() => {
    const entryTenantId = tenantId ?? selectedTenantId;
    if (!userId || !entryTenantId || !projectName || !appName || !workflowName) return;

    const identity = {
      userId,
      tenantId: entryTenantId,
      project: projectName,
      app: appName,
      workflow: workflowName,
    };

    // Idempotent: only mutate the store when identity actually changes.
    const current = useWorkflowStore.getState().currentWorkflowIdentity;
    const identityChanged = !(
      current &&
      current.userId === identity.userId &&
      current.tenantId === identity.tenantId &&
      current.project === identity.project &&
      current.app === identity.app &&
      current.workflow === identity.workflow
    );

    if (identityChanged) {
      const prefs = useWorkflowViewPrefsStore.getState();
      const resolved = prefs.getOrientation(identity) ?? prefs.getDefault();
      useWorkflowStore.setState({
        layoutOrientation: resolved,
        currentWorkflowIdentity: identity,
      });

      // Cover the case where the workflow finished loading BEFORE the identity
      // resolved (e.g., bare `/location/...` URLs where the tenant is picked
      // asynchronously). `setWorkflowFiles` only migrates embedded positions
      // when identity is already set, so re-run the migration now that it is.
      useWorkflowStore.getState().migrateLegacyPositions(identity);
    }

    // Apply the `nodesMetadata.json` sidecar (if any) to the just-loaded
    // workflow. The sidecar is the source of truth for client-side state
    // (positions, swaggerPathTemplate, detached, layoutMode); applying it
    // here overrides any matching values still embedded in legacy workflow
    // JSON. Pick the branch that matches the currently-rendered mode
    // (draft view loads draft branch; published view loads published).
    // Runs every time `data` changes so a fresh fetch can re-seed the
    // store even if the identity stayed the same.
    const sidecar = data?.sidecar;
    if (sidecar) {
      const mode = useWorkflowStore.getState().currentMode;
      const branch = sidecar[mode];
      const liveWorkflow = useWorkflowStore.getState().workflow;
      if (liveWorkflow) {
        const { workflow: enriched } = applySidecarBranch(branch, liveWorkflow, identity, mode);
        if (enriched && enriched !== liveWorkflow) {
          useWorkflowStore.setState({ workflow: enriched });
        }
      }
    }
    // Seed the process-local sidecar cache so `useDraftSave` can skip the
    // pre-save GET. We seed `emptyNodesMetadata()` when the fetch returned
    // nothing — saves still need a `previous` value to merge against, and
    // an empty file is the right starting point for a workflow that's
    // never had a sidecar written.
    if (data) {
      setCachedSidecar(composeWorkflowKey(identity), sidecar ?? emptyNodesMetadata());
    }

    // Apply the draft-only `mockData.json` sidecar. Mocks exist only for the
    // draft — never hydrate them in published view (`setWorkflowFiles` already
    // reset the slice to empty, which is the correct published state). Use
    // `hydrateMocks` so this load does NOT mark the workflow dirty.
    const mockData = data?.mockData ?? null;
    if (data) {
      const mode = useWorkflowStore.getState().currentMode;
      if (mode === 'draft' && mockData) {
        useWorkflowStore
          .getState()
          .hydrateMocks(mockData.mockedNodes, /* mockRunEnabled */undefined);
      }
      // Seed the cache so the draft↔published switch can re-hydrate mocks
      // without a network roundtrip.
      setCachedMockData(composeWorkflowKey(identity), mockData ?? emptyMockData());
    }
  }, [userId, tenantId, selectedTenantId, projectName, appName, workflowName, data]);

  // Track if initial sync has completed - only check sync once on initial load
  // After initial sync, we don't want to re-check because the store will change
  // as the user edits the workflow (adds/deletes nodes), but query data stays stale
  const hasInitialSyncCompletedRef = useRef(false);
  const lastWorkflowNameRef = useRef(workflowName);

  // Track the last data object identity that was synced to prevent duplicate syncs
  const lastSyncedDataRef = useRef<typeof data | null>(null);

  // Reset sync flag synchronously when workflowName changes (before render completes)
  // This ensures we show loading spinner when navigating between workflows
  if (workflowName !== lastWorkflowNameRef.current) {
    hasInitialSyncCompletedRef.current = false;
    lastSyncedDataRef.current = null; // Reset so new workflow data will be synced
    lastWorkflowNameRef.current = workflowName;
  }

  // Clear workflow, connections, and draft mode state when leaving page
  useEffect(() => {
    return () => {
      // Order matters: clear workflowStore.selectedNode BEFORE wiping the
      // node editor, so the NodeEditor sync effect that re-opens on
      // `(selectedNode && !isOpen)` doesn't fire from a stale selection
      // between the two store updates. `clearWorkflow()` now resets
      // selection as part of its payload, but we call `clearSelection()`
      // first explicitly to keep the invariant obvious and defend against
      // future refactors of `clearWorkflow`.
      useWorkflowStore.getState().clearSelection();
      useNodeEditorStore.getState().resetEditor();
      useScopeCollapseStore.getState().clearAll();
      useMonitoringStore.getState().clearSelection();
      clearWorkflow();
      clearConnections();
      resetDraftMode();
      // Also clear any loaded execution state so the ExecutionModeBanner
      // doesn't leak a stale runId into the next page.
      useEditorRunStore.getState().clearExecution();
      hasInitialSyncCompletedRef.current = false;
      lastSyncedDataRef.current = null;
    };
  }, [clearWorkflow, clearConnections, resetDraftMode]);

  // Clear stale per-workflow store state when switching between workflows in
  // place (e.g. via the breadcrumb dropdown or after creating a new workflow
  // from the monitoring view). WorkflowDesignerPage stays mounted across
  // workflow changes, so the unmount cleanup above doesn't fire — without this:
  //   - the ExecutionModeBanner would still show the previous workflow's runId,
  //   - the expanded NodeEditor would still display the previous workflow's
  //     selected action,
  //   - scope collapse state (keyed by action name) would leak across
  //     workflows that share default-named actions ("Foreach", "Until_1", ...),
  //   - the monitoring action panel would persist a stale selectedNodeId.
  // Key off the full route identity (tenant/location/project/app/workflow) so
  // we also clear when the user switches apps or projects but happens to land
  // on a workflow with the same name. Use URL params directly (not the store
  // fallbacks) so this doesn't fire on initial mount as store context hydrates.
  // Skip the initial mount so we don't clobber a `?debugRun=` URL load.
  const routeIdentity = `${tenantId ?? ''}/${locationName ?? ''}/${urlProject ?? ''}/${urlApp ?? ''}/${workflowName ?? ''}`;
  const prevRouteIdentityRef = useRef(routeIdentity);
  useEffect(() => {
    if (prevRouteIdentityRef.current !== routeIdentity) {
      // Selection FIRST, editor SECOND — same ordering rationale as the
      // unmount cleanup above and as `nodeEditorStore.closeEditor`.
      useWorkflowStore.getState().clearSelection();
      useNodeEditorStore.getState().resetEditor();
      useScopeCollapseStore.getState().clearAll();
      useMonitoringStore.getState().clearSelection();
      useEditorRunStore.getState().clearExecution();
      prevRouteIdentityRef.current = routeIdentity;
    }
  }, [routeIdentity]);

  // Check if store is synced with query data (only for initial load)
  // This prevents rendering the designer before the workflow is in the store.
  // IMPORTANT: We can't compare raw query data with the store directly because
  // setWorkflowFiles() normalizes the workflow (strips default retryPolicy, fixes
  // nested inputs, etc.), so isEqual(rawData, normalizedStore) would fail for
  // workflows that needed normalization — causing an infinite loading spinner.
  const isStoreSynced = useMemo(() => {
    // Once initial sync is done, always return true
    if (hasInitialSyncCompletedRef.current) return true;

    if (!data?.prodWorkflow) return false;
    if (!storeWorkflow) return false;

    // lastSyncedDataRef is reset to null whenever workflowName changes (in the
    // useEffect that calls clearWorkflow). If it is still null here, the store
    // holds stale data from the PREVIOUS workflow — report not-synced so the
    // loading spinner stays visible and the effect can safely call
    // setWorkflowFiles() for the new workflow.
    if (lastSyncedDataRef.current === null) return false;

    // The sync effect has run (lastSyncedDataRef is set) and the store has a
    // workflow — the store content is the normalized version of the query data.
    // Trust the effect rather than comparing raw vs. normalized (which can
    // diverge when normalization strips fields like retryPolicy).
    return true;
  }, [data, storeWorkflow]);

  // Mark initial sync as complete in an effect (not inside useMemo, which must be side-effect free)
  useEffect(() => {
    if (isStoreSynced && !hasInitialSyncCompletedRef.current) {
      hasInitialSyncCompletedRef.current = true;
    }
  }, [isStoreSynced]);

  const handleBack = () => {
    navigate('/');
  };

  // Show error state when no project/app context is available (not even from URL)
  if (!projectName || !appName) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <Text>Failed to load workflow</Text>
          <Text size={200}>No Logic App selected</Text>
          <Button appearance="primary" onClick={handleBack}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Show loading while query is fetching OR while store hasn't synced yet
  if (isLoading || (data && !isStoreSynced)) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <Spinner size="medium" label="Loading workflow..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <Text>Failed to load workflow</Text>
          <Text size={200}>{error instanceof Error ? error.message : 'Unknown error'}</Text>
          <Button appearance="primary" onClick={handleBack}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Get debug run ID from query params if present
  const debugRunId = searchParams.get('debugRun');

  return (
    <div className={styles.container} data-testid="workflow-designer-loaded">
      <WorkflowDesignerWithCode
        initialRunId={runId}
        initialDebugRunId={debugRunId ?? undefined}
      />
    </div>
  );
};
